// src/components/admin/CooperativaWizardModal.js
// Wizard de 4 pasos para crear o editar una cooperativa:
//   1. Datos generales
//   2. Documentos (RUT + cédula del representante, con OCR)
//   3. Datos contrato (NIT, RepLegal, cédula rep, cuenta bancaria)
//   4. Ubicación (país/departamento/municipio + mapa con lat/lng)
//
// Reemplaza el <Dialog> inline anterior de AdminDashboard.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Grid, TextField, Button, IconButton,
  Stepper, Step, StepLabel, Alert, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Chip, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import { getDepartamentos, getMunicipios } from '../../services/ecolabApi';
import UbicacionMapaPicker from '../UbicacionMapaPicker';
import VerificadorDocumento from '../VerificadorDocumento';

const STEPS = ['Datos generales', 'Documentos', 'Datos contrato', 'Ubicación'];

const FORM_INICIAL = {
  cooperativaID:        null,
  federacionID:         1,
  nombre:               '',
  siglas:               '',
  tipoEntidad:          'COOPERATIVA',
  descripcion:          '',
  email:                '',
  telefono:             '',
  sitioWeb:             '',
  // Contrato
  nit:                  '',
  representanteLegal:   '',
  cedulaRepresentante:  '',
  cuentaBancaria:       '',
  // Ubicación
  paisID:               1045,     // Colombia por defecto
  departamentoID:       '',
  municipioID:          '',
  direccion:            '',
  latitud:              '',
  longitud:             '',
};

export default function CooperativaWizardModal({
  open, modo = 'crear', initialData = null,
  onClose, onSubmit, onToast,
}) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(FORM_INICIAL);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const [deptos, setDeptos] = useState([]);
  const [munis, setMunis]   = useState([]);

  // Documentos verificados por OCR
  const [docs, setDocs] = useState({
    rut:       { file: null, url: null },
    cedulaRep: { file: null, url: null },
  });
  const [snapshotRut, setSnapshotRut] = useState(null);
  const [snapshotCed, setSnapshotCed] = useState(null);
  const [docsInvalidadosAviso, setDocsInvalidadosAviso] = useState(false);
  // Marcar cédula del representante como pendiente para aprobación posterior.
  // La cooperativa se creará con EstadoDocumentos=PENDIENTE_CEDULA_REP y solo
  // un SuperAdmin o AdminFederacion podrá completarla luego.
  const [cedulaRepPendiente, setCedulaRepPendiente]   = useState(false);
  const [dlgPendiente, setDlgPendiente] = useState(false);

  const isEditar = modo === 'editar';

  // Cargar datos iniciales al abrir
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setError(null);
    setDocsInvalidadosAviso(false);
    if (initialData) {
      setForm({
        ...FORM_INICIAL,
        ...initialData,
        latitud:  initialData.latitud  != null ? String(initialData.latitud)  : '',
        longitud: initialData.longitud != null ? String(initialData.longitud) : '',
      });
    } else {
      setForm(FORM_INICIAL);
    }
    setDocs({ rut: { file: null, url: null }, cedulaRep: { file: null, url: null } });
    setSnapshotRut(null);
    setSnapshotCed(null);
    setCedulaRepPendiente(false);
    setDlgPendiente(false);
  }, [open, initialData]);

  useEffect(() => {
    if (!open) return;
    getDepartamentos(form.paisID || 1045).then(setDeptos).catch(() => setDeptos([]));
  }, [open, form.paisID]);

  useEffect(() => {
    if (!form.departamentoID) { setMunis([]); return; }
    getMunicipios(form.departamentoID).then(setMunis).catch(() => setMunis([]));
  }, [form.departamentoID]);

  const set = useCallback((campo, valor) => setForm(f => ({ ...f, [campo]: valor })), []);

  const setLatLng = useCallback(({ lat, lng }) => {
    setForm(f => ({
      ...f,
      latitud:  lat != null ? String(lat) : '',
      longitud: lng != null ? String(lng) : '',
    }));
  }, []);

  // Invalidación de docs si cambian datos que se cruzan por OCR
  useEffect(() => {
    if (!docs.rut.url || !snapshotRut) return;
    const cambio = (form.nombre || '').trim() !== snapshotRut.nombre ||
                   (form.nit    || '').trim() !== snapshotRut.nit;
    if (cambio) {
      setDocs(p => ({ ...p, rut: { file: null, url: null } }));
      setSnapshotRut(null);
      setDocsInvalidadosAviso(true);
    }
  }, [form.nombre, form.nit, docs.rut.url, snapshotRut]);

  useEffect(() => {
    if (!docs.cedulaRep.url || !snapshotCed) return;
    const cambio = (form.representanteLegal  || '').trim() !== snapshotCed.rep ||
                   (form.cedulaRepresentante || '').trim() !== snapshotCed.cedula;
    if (cambio) {
      setDocs(p => ({ ...p, cedulaRep: { file: null, url: null } }));
      setSnapshotCed(null);
      setDocsInvalidadosAviso(true);
    }
  }, [form.representanteLegal, form.cedulaRepresentante, docs.cedulaRep.url, snapshotCed]);

  // Validación por paso
  const puedeAvanzar = useMemo(() => {
    if (step === 0) return !!(form.nombre.trim() && form.email.trim());
    // Paso Documentos: RUT siempre obligatorio, cédula del representante puede
    // estar OCR-verificada O marcada como pendiente (aprobación posterior).
    if (step === 1) return !!(docs.rut.url && (docs.cedulaRep.url || cedulaRepPendiente));
    if (step === 2) return !!(form.nit.trim() && form.representanteLegal.trim() && form.cedulaRepresentante.trim());
    if (step === 3) return !!(form.municipioID);
    return false;
  }, [step, form, docs, cedulaRepPendiente]);

  const atras = () => {
    setStep(s => {
      // Al retroceder desde Documentos, invalidar y forzar re-subida
      if (s === 1) {
        setDocs({ rut: { file: null, url: null }, cedulaRep: { file: null, url: null } });
        setSnapshotRut(null);
        setSnapshotCed(null);
        setDocsInvalidadosAviso(true);
      }
      return Math.max(s - 1, 0);
    });
  };
  const siguiente = () => setStep(s => Math.min(s + 1, STEPS.length - 1));

  const enviar = async () => {
    setError(null);
    setEnviando(true);
    try {
      const dto = {
        ...form,
        federacionId:  form.federacionID || 1,
        latitud:  form.latitud  ? parseFloat(form.latitud)  : null,
        longitud: form.longitud ? parseFloat(form.longitud) : null,
        municipioID:    form.municipioID    ? parseInt(form.municipioID, 10)    : null,
        departamentoID: form.departamentoID ? parseInt(form.departamentoID, 10) : null,
        paisID:         form.paisID         ? parseInt(form.paisID, 10)         : null,
        urlRut:                 docs.rut.url || undefined,
        // Si está marcada como pendiente, no mandar el URL de la cédula
        urlCedulaRepresentante: cedulaRepPendiente ? undefined : (docs.cedulaRep.url || undefined),
        cedulaRepPendiente:     cedulaRepPendiente,
      };
      await onSubmit?.(dto);
      onClose?.();
    } catch (e) {
      setError(e?.message || 'No se pudo guardar la cooperativa.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{
        background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)',
        color: '#fff', display:'flex', alignItems:'center', justifyContent:'space-between', py: 1.5,
      }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
          <BusinessIcon />
          <Box>
            <Typography sx={{ fontWeight:700, lineHeight:1.2 }}>
              {isEditar ? 'Editar cooperativa' : 'Nueva cooperativa'}
            </Typography>
            <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.85)' }}>
              Datos generales, documentos, contrato y ubicación
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color:'#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: '32px !important' }}>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
          {STEPS.map(s => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Paso 1 — Datos generales */}
        {step === 0 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Datos generales</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField size="small" fullWidth required label="Nombre de la cooperativa"
                  value={form.nombre} onChange={e => set('nombre', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField size="small" fullWidth label="Siglas"
                  value={form.siglas} onChange={e => set('siglas', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Tipo de entidad</InputLabel>
                  <Select value={form.tipoEntidad} label="Tipo de entidad"
                    onChange={e => set('tipoEntidad', e.target.value)}>
                    <MenuItem value="COOPERATIVA">Cooperativa</MenuItem>
                    <MenuItem value="FEDERACION">Federación</MenuItem>
                    <MenuItem value="CONFEDERACION">Confederación</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth required label="Email"
                  value={form.email} onChange={e => set('email', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Teléfono"
                  value={form.telefono} onChange={e => set('telefono', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Sitio web"
                  value={form.sitioWeb} onChange={e => set('sitioWeb', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Descripción" multiline rows={3}
                  value={form.descripcion} onChange={e => set('descripcion', e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Paso 2 — Documentos */}
        {step === 1 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Documentos de la cooperativa</Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              Debemos verificar que los datos coinciden con los documentos oficiales.
            </Typography>

            {docsInvalidadosAviso && (
              <Alert severity="warning" sx={{ mb: 2, fontSize: '0.85rem' }}
                onClose={() => setDocsInvalidadosAviso(false)}>
                🔒 Por seguridad, debe volver a subir los documentos. Cambió algún dato clave.
              </Alert>
            )}

            <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>RUT de la cooperativa</Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1.5 }}>
                Suba el RUT de <strong>{form.nombre || '—'}</strong> (NIT <strong>{form.nit || '(defínalo en el paso 3)'}</strong>).
                El NIT y el nombre deben aparecer en el documento.
              </Typography>
              <VerificadorDocumento
                titulo=""
                descripcion="Formatos aceptados: PDF, JPG, PNG. Máximo 10MB."
                cedula={form.nit}
                nombres={form.nombre}
                apellidos=""
                tipoDocumento="rut"
                onValido={(file, resultado) => {
                  if (resultado?.urlDocumento) {
                    setDocs(p => ({ ...p, rut: { file, url: resultado.urlDocumento } }));
                    setSnapshotRut({ nombre: (form.nombre || '').trim(), nit: (form.nit || '').trim() });
                    setDocsInvalidadosAviso(false);
                  }
                }}
                onInvalido={() => {
                  setDocs(p => ({ ...p, rut: { file: null, url: null } }));
                  setSnapshotRut(null);
                }}
              />
            </Box>

            <Box sx={{ mb: 2, p: 2, borderRadius: 2,
              border: cedulaRepPendiente ? '2px dashed #ed6c02' : '1px solid #e0e0e0',
              background: cedulaRepPendiente ? '#fff8e1' : 'transparent' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontWeight: 700 }}>Cédula del representante legal (ambas caras)</Typography>
                {!cedulaRepPendiente && !docs.cedulaRep.url && (
                  <Button size="small" variant="outlined" color="warning"
                    onClick={() => setDlgPendiente(true)}
                    sx={{ fontSize: '0.72rem', textTransform: 'none' }}>
                    Marcar como pendiente
                  </Button>
                )}
                {cedulaRepPendiente && (
                  <Button size="small" variant="text" color="warning"
                    onClick={() => setCedulaRepPendiente(false)}
                    sx={{ fontSize: '0.72rem', textTransform: 'none' }}>
                    Deshacer (subir ahora)
                  </Button>
                )}
              </Box>

              {cedulaRepPendiente ? (
                <Alert severity="warning" sx={{ mt: 1, fontSize: '0.85rem' }}>
                  <strong>⚠️ Cédula marcada como pendiente.</strong> La cooperativa se creará pero quedará
                  <strong> sujeta a aprobación</strong> por un SuperAdmin o AdminFederacion, quien deberá
                  subir la cédula del representante posteriormente. Se enviarán notificaciones automáticas.
                </Alert>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                    📋 Suba la cédula escaneada por <strong>ambas caras (frente y reverso) en un solo archivo PDF</strong>.
                  </Alert>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1.5 }}>
                    Cédula de <strong>{form.representanteLegal || '(defínalo en el paso 3)'}</strong>
                    {' '}(CC <strong>{form.cedulaRepresentante || '—'}</strong>). Los datos deben coincidir exactamente.
                  </Typography>
                  <VerificadorDocumento
                    titulo=""
                    descripcion="Formato preferido: PDF con ambas caras. También se aceptan JPG y PNG. Máximo 10MB."
                    cedula={form.cedulaRepresentante}
                    nombres={form.representanteLegal}
                    apellidos=""
                    tipoDocumento="cedula"
                    onValido={(file, resultado) => {
                      if (resultado?.urlDocumento) {
                        setDocs(p => ({ ...p, cedulaRep: { file, url: resultado.urlDocumento } }));
                        setSnapshotCed({
                          rep:    (form.representanteLegal || '').trim(),
                          cedula: (form.cedulaRepresentante || '').trim(),
                        });
                        setDocsInvalidadosAviso(false);
                      }
                    }}
                    onInvalido={() => {
                      setDocs(p => ({ ...p, cedulaRep: { file: null, url: null } }));
                      setSnapshotCed(null);
                    }}
                  />
                </>
              )}
            </Box>
          </Box>
        )}

        {/* Diálogo de confirmación: marcar cédula del representante como pendiente */}
        <Dialog open={dlgPendiente} onClose={() => setDlgPendiente(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ color: '#ed6c02' }}>⚠️ Marcar cédula como pendiente</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              La cooperativa <strong>se creará</strong>, pero quedará marcada como <strong>PENDIENTE DE
              APROBACIÓN</strong> hasta que un SuperAdmin o el AdminFederacion suba la cédula del
              representante y confirme.
            </Alert>
            <Typography variant="body2" sx={{ mb: 1 }}>Al confirmar, se hará lo siguiente:</Typography>
            <Box component="ul" sx={{ pl: 3, fontSize: '0.85rem', color: '#555' }}>
              <li>Se guardará la cooperativa con estado <code>PENDIENTE_CEDULA_REP</code>.</li>
              <li>Se registrará en <strong>auditoría</strong>.</li>
              <li>Se enviarán <strong>notificaciones y correos</strong> al SuperAdmin y a los AdminFederacion.</li>
              <li>Recibirás una copia del email como creador.</li>
            </Box>
            <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 2 }}>
              Puedes deshacer esta acción antes de crear la cooperativa haciendo clic en "Deshacer".
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDlgPendiente(false)}>Cancelar</Button>
            <Button variant="contained" color="warning"
              onClick={() => {
                setCedulaRepPendiente(true);
                setDocs(p => ({ ...p, cedulaRep: { file: null, url: null } }));
                setSnapshotCed(null);
                setDlgPendiente(false);
              }}>
              Sí, marcar pendiente
            </Button>
          </DialogActions>
        </Dialog>

        {/* Paso 3 — Datos contrato */}
        {step === 2 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Datos para el contrato</Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              Información legal oficial de la cooperativa. Se usa para generar el PDF de contratos
              con los afiliados y aliados.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth required label="NIT"
                  value={form.nit} onChange={e => set('nit', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth required label="Representante legal"
                  value={form.representanteLegal} onChange={e => set('representanteLegal', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth required label="Cédula del representante"
                  value={form.cedulaRepresentante} onChange={e => set('cedulaRepresentante', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label='Cuenta bancaria (ej: "Banco X · Ahorros 12345")'
                  value={form.cuentaBancaria} onChange={e => set('cuentaBancaria', e.target.value)} />
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }}><Chip label="Aviso" size="small" /></Divider>
            <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
              Al cambiar el NIT o el nombre después de haber subido el RUT, deberá volver a
              cargar el documento en el paso Documentos.
            </Alert>
          </Box>
        )}

        {/* Paso 4 — Ubicación */}
        {step === 3 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Ubicación</Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              La ubicación de la cooperativa define su área geográfica de cobertura para aliados y afiliados.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth required>
                  <InputLabel>Departamento</InputLabel>
                  <Select value={form.departamentoID || ''} label="Departamento"
                    onChange={e => { set('departamentoID', e.target.value); set('municipioID', ''); }}>
                    {deptos.map(d => (
                      <MenuItem key={d.departamentoID} value={d.departamentoID}>{d.nombre}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth required disabled={!form.departamentoID}>
                  <InputLabel>Municipio</InputLabel>
                  <Select value={form.municipioID || ''} label="Municipio"
                    onChange={e => set('municipioID', e.target.value)}>
                    {munis.map(m => (
                      <MenuItem key={m.municipioID} value={m.municipioID}>{m.nombre}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Dirección"
                  value={form.direccion} onChange={e => set('direccion', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <UbicacionMapaPicker
                  latitud={form.latitud}
                  longitud={form.longitud}
                  onCambio={setLatLng}
                  direccionParaGeocode={form.direccion}
                  ciudadParaGeocode={munis.find(m => m.municipioID == form.municipioID)?.nombre}
                  departamentoParaGeocode={deptos.find(d => d.departamentoID == form.departamentoID)?.nombre}
                  alto={320}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={enviando}>Cancelar</Button>
        <Box sx={{ flex: 1 }} />
        {step > 0 && (<Button onClick={atras} disabled={enviando}>Atrás</Button>)}
        {step < STEPS.length - 1 && (
          <Button variant="contained" onClick={siguiente} disabled={!puedeAvanzar}
            sx={{ bgcolor:'#2e7d32', '&:hover': { bgcolor:'#1a4d2e' } }}>
            Siguiente
          </Button>
        )}
        {step === STEPS.length - 1 && (
          <Button variant="contained" onClick={enviar} disabled={!puedeAvanzar || enviando}
            startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ bgcolor:'#2e7d32', '&:hover': { bgcolor:'#1a4d2e' } }}>
            {isEditar ? 'Guardar cambios' : 'Crear cooperativa'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
